import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  LegalContainer,
  LegalInner,
  HeaderTitle,
  Subtle,
  BoxDiv,
  SectionTitle,
  CrossLinks,
  PageLink,
} from "./legalStyles";

const LAST_UPDATED = "15 October 2026";
const CONTACT_EMAIL = "nassos@duck.com";

const TermsPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    document.title = "NΛSOS · Terms";
  }, []);

  // /terms#returns is the returns link the store hands out.
  useEffect(() => {
    if (!hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView();
  }, [hash]);

  return (
    <LegalContainer>
      <LegalInner>
        <HeaderTitle>Terms</HeaderTitle>
        <Subtle>Last updated: {LAST_UPDATED}</Subtle>
        <CrossLinks>
          <PageLink to="/privacy">Privacy Policy</PageLink>
          <PageLink to="/cookies">Cookie Policy</PageLink>
        </CrossLinks>

        <BoxDiv>
          <p>
            <strong>Short version:</strong> n450s.com is a personal site, run by one person
            for a small group of friends. Be kind, don&apos;t break things, and if something
            you bought isn&apos;t right, tell me and we&apos;ll sort it out.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Using the site</SectionTitle>
          <p>
            You can use n450s.com for free. Keep your account to yourself, don&apos;t post
            anything illegal or hurtful, and don&apos;t try to break or overload the site. I
            may remove content or close an account that doesn&apos;t follow this.
          </p>
          <p>
            The site is a hobby. Features come and go, things sometimes break, and it comes
            with no promise that it will always be online or free of mistakes.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>The store</SectionTitle>
          <p>
            The store sells things made around n450s.com: one-off clothes, sticker packs and
            prints of Art of the Day. Prices are in euros and the price shown at checkout is
            what you pay, delivery included.
          </p>
          <p>
            One-off pieces exist once. Photos are as close as I could get them, but colours
            can look a little different on your screen.
          </p>
          <p>
            Prints are made to order from the day&apos;s digital art. Each one is numbered
            when it is printed, so it can take one to two weeks to reach you. The original
            1 of 1 of a day can only be ordered by that day&apos;s author.
          </p>
          <p>
            Buying a print gets you the print. It doesn&apos;t give you the rights to the
            art; see{" "}
            <PageLink to="/art-of-the-day/termsandownership">
              Art of the Day terms and ownership
            </PageLink>
            .
          </p>
        </BoxDiv>

        <BoxDiv id="pre-orders">
          <SectionTitle>Pre-orders</SectionTitle>
          <p>
            Some things can be pre-ordered before they are on sale, like prints of Art of the
            Day. A pre-order costs nothing and needs no address, but it is a promise: when the
            thing goes on sale, you take it with your next order on n450s.com.
          </p>
          <p>
            You can change how many you want at any time. You can also remove a pre-order, but
            that is final: once you remove it, you can&apos;t pre-order or buy that print again
            (that day, in that format).
          </p>
          <p>
            Trading cards are gathered rather than pre-ordered one at a time, one card per day.
            Each card you gather is a promise like a pre-order, and putting one back is final in
            the same way. Once you have gathered 21, you can pre-order them as a set, and the set
            goes into the next print run.
          </p>
          <p>
            If the price goes up from what you saw when you pre-ordered, or I can&apos;t make
            something you pre-ordered, I&apos;ll tell you, and you can drop it without losing the
            chance to buy it later.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Orders, payment and delivery</SectionTitle>
          <p>
            The shop runs on 450labs, which keeps the orders and handles payment. You can pay
            in cash when your order arrives, or by card when that option is shown. Card
            details go to the payment provider and never to n450s.com.
          </p>
          <p>
            If something can&apos;t be delivered (it sold out, or there was a mistake in the
            price), I&apos;ll tell you and cancel the order, and give back anything you paid.
          </p>
          <p>
            I deliver by hand or by courier. If an order is running late, I&apos;ll let you
            know.
          </p>
        </BoxDiv>

        <BoxDiv id="returns">
          <SectionTitle>Returns</SectionTitle>
          <p>
            Clothes and stickers: you can send them back unused within 14 days of getting
            them, and you get your money back.
          </p>
          <p>
            Prints: if one arrives damaged or isn&apos;t what you ordered, tell me within 14
            days and I&apos;ll print it again or give you your money back. Because each print
            is made and numbered for you, I can&apos;t take one back just because you changed
            your mind, but ask anyway.
          </p>
          <p>
            To return something or report a problem, email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Your details</SectionTitle>
          <p>
            For an order I need your name, phone, email and address. They go to 450labs to
            run the order and are used only to deliver it. More in the{" "}
            <PageLink to="/privacy">Privacy Policy</PageLink>.
          </p>
        </BoxDiv>

        <BoxDiv>
          <SectionTitle>Changes and contact</SectionTitle>
          <p>
            These terms may change as the site does; the date at the top changes with them.
            Questions go to <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </BoxDiv>

        <CrossLinks>
          <PageLink to="/privacy">Privacy Policy</PageLink>
          <PageLink to="/cookies">Cookie Policy</PageLink>
        </CrossLinks>
      </LegalInner>
    </LegalContainer>
  );
};

export default TermsPage;
